import React, { useEffect, useState } from 'react';
import { useAppStore } from '../../App';

interface MyBarterFamily {
 moFamilyHash: string;
 name: string;
 description: string | null;
 totalSlots: number;
}

interface Props {
 listingHash: string;
 listingTitle: string;
 recipientPeerHash: string;
 onClose: () => void;
 onCreated?: (proposalHash: string, conversationId: string) => void;
}

/**
 * 바터(Type 3) 제안 모달.
 * 사용자가 자기 보유한 family_type='barter' 모패밀리 (still unpaired) 중 하나를 선택해
 * listing 의 가상 모패밀리와 매칭 신청.
 *
 * unpaired 가 0개면 "기본 바터 모패밀리 만들고 제안 보내기" 단일 액션 — 즉시 새 가상
 * 패밀리를 만들고 그 hash 로 propose 진행.
 */
const BarterProposeModal: React.FC<Props> = ({
 listingHash, listingTitle, recipientPeerHash, onClose, onCreated,
}) => {
 const { user } = useAppStore();
 const ownerName = user?.name || user?.email?.split('@')[0] || '나';

 const [families, setFamilies] = useState<MyBarterFamily[]>([]);
 const [loading, setLoading] = useState(true);
 const [selected, setSelected] = useState<string>('');
 const [message, setMessage] = useState('');
 const [submitting, setSubmitting] = useState(false);
 const [error, setError] = useState<string | null>(null);

 useEffect(() => {
  fetch('/market/listings/me/barter-unpaired', { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    const rows = (j.data ?? []) as any[];
    const barters = rows.map((f: any): MyBarterFamily => ({
     moFamilyHash: f.mo_family_hash,
     name: f.name,
     description: f.description ?? null,
     totalSlots: f.total_slots ?? 1,
    }));
    setFamilies(barters);
    if (barters.length > 0) setSelected(barters[0].moFamilyHash);
   })
   .catch(err => setError(err.message || '내 바터 모패밀리 조회 실패'))
   .finally(() => setLoading(false));
 }, []);

 const sendProposal = async (senderFamilyHash: string): Promise<{ proposalHash: string; conversationId: string }> => {
  const today = new Date().toISOString().slice(0, 10);
  const month = new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10);
  const res = await fetch('/market/proposals', {
   method: 'POST',
   headers: { 'Content-Type': 'application/json' },
   credentials: 'include',
   body: JSON.stringify({
    type: 3,
    listing_hash: listingHash,
    recipient_mopeer: recipientPeerHash,
    payload: {
     sender_barter_family_hash: senderFamilyHash,
     period_start: today,
     period_end: month,
     message: message.trim() || undefined,
    },
   }),
  });
  const j = await res.json();
  if (!res.ok || !j.success) throw new Error(j.error || '제안 전송 실패');
  return { proposalHash: j.data.proposal_hash, conversationId: j.data.conversation_id };
 };

 const submit = async () => {
  if (!selected) {
   setError('내 바터 모패밀리를 선택하세요.');
   return;
  }
  setSubmitting(true);
  setError(null);
  try {
   const { proposalHash, conversationId } = await sendProposal(selected);
   onCreated?.(proposalHash, conversationId);
  } catch (err: any) {
   setError(err.message || '제안 전송 실패');
  } finally {
   setSubmitting(false);
  }
 };

 const submitAuto = async () => {
  setSubmitting(true);
  setError(null);
  try {
   // 1. 기본 바터 모패밀리 빠른 생성 (listing 없이, 자동 위임)
   const r = await fetch('/market/listings/barter-family', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ total_slots: 1 }),
   });
   const j = await r.json();
   if (!r.ok || !j.success) throw new Error(j.error || '바터 모패밀리 생성 실패');
   const newHash = j.data.mo_family_hash as string;
   // 2. 그 hash 로 제안
   const { proposalHash, conversationId } = await sendProposal(newHash);
   onCreated?.(proposalHash, conversationId);
  } catch (err: any) {
   setError(err.message || '제안 전송 실패');
  } finally {
   setSubmitting(false);
  }
 };

 return (
  <div
   className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
   onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
   <div className="bg-white p-3 md:p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
    <div className="flex justify-between items-center mb-3">
     <h2 className="text-lg md:text-xl font-bold">바터 제안</h2>
     <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
    </div>

    <p className="text-sm text-gray-600 mb-3">
     <span className="font-medium">{listingTitle}</span> 매물에 보낼 바터 제안입니다.
     수락 시 양측 슬롯이 짝에게 독점 위임됩니다 (짝 슬롯 운영용 가상 모패밀리가 자동 생성).
    </p>

    {loading ? (
     <p className="text-sm text-gray-400">로딩 중...</p>
    ) : families.length === 0 ? (
     <div className="space-y-3">
      <div className="bg-yellow-50 border border-yellow-200 p-3 text-xs text-yellow-800">
       사용할 바터 모패밀리가 없어 새로 만들어 제안합니다. 가상 모패밀리 <strong>"{ownerName} 바터"</strong> 가 1슬롯으로 만들어지고, 본인 기본 모패밀리의 빈 슬롯 1개를 자동 독점 위임받습니다.
      </div>
      <div>
       <label className="block text-xs text-gray-600 mb-1">메시지 (선택)</label>
       <textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        rows={3}
        placeholder="상대에게 전할 짧은 메시지"
        className="w-full px-2 py-1 border border-gray-300 text-sm"
       />
      </div>
      {error && <div className="bg-red-50 text-red-700 p-2 text-xs">{error}</div>}
      <div className="flex gap-2 pt-2">
       <button
        onClick={submitAuto}
        disabled={submitting}
        className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
       >
        {submitting ? '진행 중...' : '기본 바터 모패밀리 만들고 제안 보내기'}
       </button>
       <button
        onClick={onClose}
        className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700"
       >
        취소
       </button>
      </div>
     </div>
    ) : (
     <div className="space-y-3">
      <div>
       <label className="block text-xs text-gray-600 mb-1">내 바터 모패밀리 선택 *</label>
       <select
        value={selected}
        onChange={e => setSelected(e.target.value)}
        className="w-full px-2 py-1 border border-gray-300 text-sm"
       >
        {families.map(f => (
         <option key={f.moFamilyHash} value={f.moFamilyHash}>
          {f.name} (슬롯 {f.totalSlots}개)
         </option>
        ))}
       </select>
      </div>

      <div>
       <label className="block text-xs text-gray-600 mb-1">메시지 (선택)</label>
       <textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        rows={3}
        placeholder="상대에게 전할 짧은 메시지"
        className="w-full px-2 py-1 border border-gray-300 text-sm"
       />
      </div>

      {error && <div className="bg-red-50 text-red-700 p-2 text-xs">{error}</div>}

      <div className="flex gap-2 pt-2">
       <button
        onClick={submit}
        disabled={submitting}
        className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
       >
        {submitting ? '전송 중...' : '제안 보내기'}
       </button>
       <button
        onClick={onClose}
        className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700"
       >
        취소
       </button>
      </div>
     </div>
    )}
   </div>
  </div>
 );
};

export default BarterProposeModal;
